'use client';

import { useQuery } from '@tanstack/react-query';
import { FetchOrderProgressResponseBody } from 'bff';
import React from 'react';
import { OrderProgressAudience } from '../constants/order-progress';
import { OrderProgressCallout } from './OrderProgressCallout';
import { OrderProgressStepper } from './OrderProgressStepper';
import { OrderStatusBadge } from './OrderStatusBadge';

export const buildOrderProgressQueryKey = (orderId: string) => [
  `order-progress-${orderId}`,
];

/**
 * Shared order detail header. It runs its own query seeded with SSR
 * `initialData`: two provider mutations never `router.refresh()`, so a header
 * fed by the RSC layout would go stale.
 */
export const OrderProgressHeader = ({
  orderId,
  refId,
  name,
  audience,
  basePath,
  initialData,
  fetchProgress,
  children,
}: {
  orderId: string;
  refId: string | null;
  name: string;
  audience: OrderProgressAudience;
  // Order detail path in this app: the stepper links hang from it.
  basePath: string;
  initialData?: FetchOrderProgressResponseBody;
  fetchProgress: () => Promise<FetchOrderProgressResponseBody>;
  // Side-specific actions (accept/reject, cancel).
  children?: React.ReactNode;
}) => {
  const query = useQuery({
    queryKey: buildOrderProgressQueryKey(orderId),
    queryFn: fetchProgress,
    initialData,
  });

  const progress = query.data;

  return (
    <div className='flex w-full flex-col gap-4 rounded-xl border bg-white p-4'>
      <div className='flex flex-col justify-between gap-2 lg:flex-row lg:items-center'>
        <div className='flex flex-wrap items-center gap-2'>
          <h2 className='text-base font-semibold'>
            {refId ? `${refId}` : 'Orden'}
            {name ? `: "${name}"` : ''}
          </h2>
          {progress && <OrderStatusBadge status={progress.order_status} />}
        </div>

        {children}
      </div>

      {progress && (
        <div className='py-2'>
          <OrderProgressStepper
            stages={progress.stages}
            audience={audience}
            basePath={basePath}
            activeStage={progress.next_action?.stage ?? null}
            workProof={progress.work_proof}
            scheduledFor={progress.scheduled_for}
          />

          {/* <OrderProgressCallout progress={progress} audience={audience} /> */}
        </div>
      )}
    </div>
  );
};
